/**
 * Mock API Service Layer
 * Replaces real AWS Lambda calls with realistic mock responses for demo purposes
 */

// Mock data types
export interface MockPayment {
  id: string
  invoiceId: string
  invoiceNumber: string
  amount: number
  stablecoinAmount: number
  stablecoinType: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  provider: 'HashKey' | 'OSL' | 'Moonpay'
  exchangeRate: number
  transactionHash?: string
  blockNumber?: number
  confirmations: number
  status: 'pending' | 'confirmed' | 'failed' | 'expired'
  settlementRail: 'FPS' | 'CIPS' | 'e-CNY'
  expiresAt?: string
  createdAt: string
  confirmedAt?: string
}

export interface MockSettlement {
  id: string
  batchId: string
  totalAmount: number
  transactionCount: number
  processingFee: number
  fpsFee: number
  netAmount: number
  providerBreakdown: Record<string, any>
  status: 'pending' | 'processing' | 'completed' | 'failed'
  fpsReference?: string
  settlementRail: 'FPS' | 'CIPS' | 'e-CNY'
  estimatedTime: string
  createdAt: string
  completedAt?: string
}

export interface MockProviderStatus {
  provider: 'HashKey' | 'OSL' | 'Moonpay'
  status: 'healthy' | 'degraded' | 'down'
  responseTime: number
  uptime: number
  lastUpdate: string
  supportedStablecoins: string[]
  supportedRails: string[]
}

export interface MockExchangeRate {
  stablecoin: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  currency: 'HKD' | 'CNY' | 'USD'
  rate: number
  spread: number
  lastUpdated: string
}

export interface MockInvoice {
  id: string
  invoiceNumber: string
  businessName: string
  businessEmail: string
  customerName: string
  customerEmail: string
  customerCountry: string
  amount: number
  currency: 'HKD' | 'CNY' | 'USD'
  description: string
  dueDate: string
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'
  settlementRail: 'FPS' | 'CIPS' | 'e-CNY'
  paymentLink: string
  qrCode: string
  createdAt: string
  sentAt?: string
  paidAt?: string
  paymentId?: string
}

export interface MockInvoiceFormData {
  customerName: string
  customerEmail: string
  customerCountry: string
  amount: number
  currency: 'HKD' | 'CNY' | 'USD'
  description: string
  dueDate: string
  settlementRail: 'FPS' | 'CIPS' | 'e-CNY'
}

// Customer Payment Flow Types
export interface MockCustomerInvoice {
  id: string
  invoiceNumber: string
  businessName: string
  businessLogo?: string
  businessEmail: string
  businessAddress: string
  amount: number
  currency: 'HKD' | 'CNY' | 'USD'
  description: string
  dueDate: string
  status: 'sent' | 'paid' | 'expired'
  createdAt: string
  customerName: string
  customerEmail: string
  customerCountry: string
  settlementRail: 'FPS' | 'CIPS' | 'e-CNY'
  paymentInstructions: string
  isExpired: boolean
  expiresAt: string
}

export interface MockPaymentOption {
  stablecoin: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  exchangeRate: number
  spread: number
  stablecoinAmount: number
  networkFee: number
  processingFee: number
  totalCost: number
  isAvailable: boolean
  estimatedTime: string
  provider: 'HashKey' | 'OSL' | 'Moonpay'
}

export interface MockCustomerPayment {
  id: string
  invoiceId: string
  amount: number
  stablecoin: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  stablecoinAmount: number
  paymentMethod: 'wallet' | 'on_ramp'
  customerAddress?: string
  businessAddress: string
  status: 'initiated' | 'pending' | 'confirmed' | 'failed'
  transactionHash?: string
  confirmations: number
  requiredConfirmations: number
  estimatedTime: string
  createdAt: string
  confirmedAt?: string
  expiresAt: string
}

export interface MockWalletBalance {
  stablecoin: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  balance: number
  formattedBalance: string
  contractAddress: string
  decimals: number
  isLoading: boolean
}

export interface MockConnectedWallet {
  address: string
  provider: string
  network: string
  isConnected: boolean
  balances: MockWalletBalance[]
  verification: {
    requiredStablecoin: string
    requiredAmount: number
    availableBalance: number
    hasSufficientBalance: boolean
    shortfall: number
  }
  connectionTime: string
}

export interface MockOnRampProvider {
  id: string
  name: string
  logo: string
  supportedStablecoins: string[]
  supportedCountries: string[]
  fees: {
    percentage: number
    fixed: number
  }
  estimatedTime: string
  isAvailable: boolean
}

export interface MockOnRampQuote {
  provider: string
  stablecoin: string
  amount: number
  fiatAmount: number
  fees: number
  totalCost: number
  estimatedTime: string
  exchangeRate: number
}

export interface MockPaymentReceipt {
  receiptId: string
  paymentId: string
  invoiceNumber: string
  businessName: string
  businessAddress: string
  customerName: string
  customerEmail: string
  amount: number
  stablecoin: string
  stablecoinAmount: number
  exchangeRate: number
  transactionHash: string
  confirmationTime: string
  settlementRail: string
  estimatedSettlement: string
  fees: {
    networkFee: number
    processingFee: number
    total: number
  }
  downloadUrl: string
  createdAt: string
}

export interface MockDashboardStats {
  totalRevenue: number
  totalInvoices: number
  totalPayments: number
  pendingSettlements: number
  revenueChange: number
  invoicesChange: number
  paymentsChange: number
  settlementsChange: number
  savingsVsTraditional: number
  averageSettlementTime: number
}

// Configuration for mock API behavior
export interface MockApiConfig {
  enabled: boolean
  baseDelay: number
  responseDelay: {
    min: number
    max: number
  }
  errorRate: number
  providerFailoverRate: number
  failoverRate: number
  enableRealtimeUpdates: boolean
}

export const defaultMockConfig: MockApiConfig = {
  enabled: true,
  baseDelay: 300,
  responseDelay: {
    min: 200,
    max: 800
  },
  errorRate: 0.05, // 5% error rate
  providerFailoverRate: 0.1, // 10% chance of provider failover
  failoverRate: 0.1,
  enableRealtimeUpdates: true
}

/**
 * Mock API Response wrapper
 */
export interface MockApiResponse<T> {
  success: boolean
  data: T
  timestamp: string
  processingTime: number
  error?: string
}

/**
 * Utility function to simulate network delay
 */
export const simulateDelay = async (config: MockApiConfig = defaultMockConfig): Promise<void> => {
  const delay = Math.random() * (config.responseDelay.max - config.responseDelay.min) + config.responseDelay.min
  await new Promise(resolve => setTimeout(resolve, delay))
}

/**
 * Utility function to simulate API errors
 */
export const shouldSimulateError = (config: MockApiConfig = defaultMockConfig): boolean => {
  return Math.random() < config.errorRate
}

/**
 * Utility function to simulate provider failover
 */
export const shouldSimulateFailover = (config: MockApiConfig = defaultMockConfig): boolean => {
  return Math.random() < config.providerFailoverRate
}

/**
 * Generate mock response wrapper
 */
export const createMockResponse = <T>(data: T, processingTime: number): MockApiResponse<T> => ({
  success: true,
  data,
  timestamp: new Date().toISOString(),
  processingTime
})

/**
 * Generate mock error response
 */
export const createMockErrorResponse = (error: string): MockApiResponse<null> => ({
  success: false,
  data: null,
  timestamp: new Date().toISOString(),
  processingTime: 0,
  error
})